/**
 * IBSPortfolioView -- the portfolio console (P348, assessment feedback P4).
 *
 * One screen across ALL IBSes: admission, tier, MVB, sealed resilience,
 * declared tolerance and the P344 STANDING live-tolerance verdict --
 * worst-first, every row one click from its story page. This is also
 * the standing home of the impact-tolerance indicators the assessment
 * wanted lifted out of the on-demand war-room.
 */
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Explainer, InfoTip } from "shared/components/Explainer";
import { NoOwnerFlag } from "shared/components/NoOwnerFlag";
import { Link } from "react-router-dom";

import { api } from "shared/api/client";
import { ToleranceBadge } from "shared/components/ToleranceBadge";
import { byIbsId, useToleranceState } from "shared/tolerance/useToleranceState";
import type { ToleranceStateRow } from "shared/tolerance/useToleranceState";
import { MvbFindingsPanel } from "twin/portfolio/MvbFindingsPanel";
import { MarsadIcon } from "shared/components/MarsadIcon";

interface PortfolioRow {
  ibs_service_id: string;
  code: string;
  name: string;
  admission_status: string;
  criticality_tier: string | null;
  mvb_flag: boolean;
  resilience: string | null;
  impact_tolerance_hours: number | null;
  live_verdict: string | null;
  live_via: string | null;
  open_findings: number;
  /** Round 20 V5. */
  owner_recorded?: boolean;
  open_critical?: number;
  last_drill_at: string | null;
  service_class?: "customer" | "state" | "internal";
  /** P428.2: the group this service is governed in, if any. */
  group_code?: string | null;
  group_name?: string | null;
}

interface Portfolio {
  /** Stitching review 5.4 (D1) -- the posture's words: in the register
   *  is admitted rows, under assessment is candidates, removed rows on
   *  neither side. Functions counted apart (round 16 L1). */
  in_register: number;
  under_assessment: number;
  functions_in_register?: number;
  functions_under_assessment?: number;
  mvb: number;
  red: number;
  breached_now: number;
  rows: PortfolioRow[];
}

/**
 * Round 11 F1 -- is a tolerance declared but not yet in force?
 *
 * The live monitor judges against the number IN FORCE, which a
 * declaration only becomes on the second signature. So an IBS with a
 * declaration in flight is unjudged for a reason that has nothing to
 * do with nobody having declared one, and saying "no tolerance
 * declared" beside the declaration itself is the row contradicting
 * itself in two adjacent cells.
 */
function toleranceInFlight(state: ToleranceStateRow | undefined): boolean {
  return state?.status === "pending";
}

const RES_TONE: Record<string, string> = {
  red: "bg-rose-100 text-rose-900 ring-rose-300",
  amber: "bg-amber-100 text-amber-900 ring-amber-300",
  green: "bg-emerald-100 text-emerald-900 ring-emerald-300",
};

const VERDICT_TONE: Record<string, string> = {
  breached: "bg-rose-100 text-rose-900 ring-rose-300",
  at_risk: "bg-amber-100 text-amber-900 ring-amber-300",
  within: "bg-emerald-100 text-emerald-900 ring-emerald-300",
};

function Pill({
  value,
  tone,
  testid,
}: {
  value: string;
  tone: Record<string, string>;
  testid?: string;
}): JSX.Element {
  return (
    <span
      data-testid={testid}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${
        tone[value] ?? "bg-slate-100 text-slate-600 ring-slate-200"
      }`}
    >
      {value.replace("_", " ")}
    </span>
  );
}

export function IBSPortfolioView(): JSX.Element {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["twin", "ibs-portfolio"],
    queryFn: () => api.get<Portfolio>("/v1/twin/ibs-portfolio"),
    refetchInterval: 60_000, // a standing console stays current
  });
  const tolerance = byIbsId(useToleranceState().data);

  const ibsRows = (data?.rows ?? []).filter((r) => r.service_class !== "internal");
  const fnRows = (data?.rows ?? []).filter(
    (r) => r.service_class === "internal" && r.admission_status !== "removed",
  );
  const renderTable = (rows: PortfolioRow[], firstColumn: string, testid: string) => (
    <div
      data-testid={testid}
      className="overflow-x-auto rounded border border-paper-2 bg-white"
    >
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-slate-400">
            <th className="px-3 py-2 text-start">{firstColumn}</th>
            <th className="px-3 py-2 text-start">
              {t("screens.iBSPortfolioView.admission")}
              <InfoTip termKey="admission" />
            </th>
            <th className="px-3 py-2 text-start">
              {t("screens.iBSPortfolioView.tier")}
              <InfoTip termKey="tier" />
            </th>
            <th className="px-3 py-2 text-start">
              {t("screens.iBSPortfolioView.resilience")}
            </th>
            <th className="px-3 py-2 text-start">
              {t("screens.iBSPortfolioView.tolerance")}
              <InfoTip termKey="impact_tolerance" />
            </th>
            <th className="px-3 py-2 text-start">
              {t("screens.iBSPortfolioView.livePosture")}
            </th>
            <th className="px-3 py-2">{t("screens.iBSPortfolioView.findings")}</th>
            <th className="px-3 py-2 text-start">
              {t("screens.iBSPortfolioView.lastDrill")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.ibs_service_id}
              data-testid={`ibs-portfolio-row-${r.code}`}
              className={r.live_verdict === "breached" ? "bg-rose-50/50" : ""}
            >
              <td className="px-3 py-1.5">
                <Link
                  to={`/twin/ibs/${r.ibs_service_id}`}
                  className="font-medium text-slate-900 hover:text-brand-brassDark hover:underline"
                >
                  {r.name}
                </Link>
                {r.admission_status === "admitted" && r.owner_recorded === false && (
                  <NoOwnerFlag openCritical={r.open_critical ?? 0} />
                )}
                {r.mvb_flag && (
                  <span className="ms-2 rounded bg-indigo-100 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-900">
                    {r.service_class === "internal"
                      ? /* Round 18 L11 -- a function is in the declaration;
                           "MVB" alone named it an MVB IBS. */
                        t("mvbMembers.inDeclaration")
                      : t("screens.iBSPortfolioView.mvb")}
                  </span>
                )}
                {r.group_name && (
                  <Link
                    to="/twin/ibs-groups"
                    data-testid={`ibs-portfolio-group-${r.code}`}
                    className="block text-xs text-slate-500 hover:text-brand-brassDark hover:underline"
                  >
                    {t("screens.iBSPortfolioView.inGroup", {
                      group: r.group_name,
                    })}
                  </Link>
                )}
              </td>
              <td className="px-3 py-1.5 text-xs text-slate-600">
                {r.admission_status}
              </td>
              <td className="px-3 py-1.5 text-xs text-slate-600">
                {r.criticality_tier?.replace("tier_", "Tier ") ?? "--"}
              </td>
              <td className="px-3 py-1.5">
                {r.resilience === "unknown" ? (
                  /* Round 14 B1 -- sealed, but nothing watched this
                           IBS: no admission, tolerance, telemetry or
                           finding. Said in words, never as green. */
                  <span
                    data-testid={`ibs-portfolio-not-assessed-${r.code}`}
                    className="text-xs text-slate-500"
                  >
                    {t("screens.iBSPortfolioView.notAssessed")}
                  </span>
                ) : r.resilience ? (
                  <Pill value={r.resilience} tone={RES_TONE} />
                ) : (
                  /* Round 10 tidy -- the same treatment its
                           neighbour got. A dash here means the
                           resilience deriver has not sealed a snapshot
                           for this IBS yet, which is a different fact
                           from the live column's (no tolerance to
                           judge against) and was equally unreadable as
                           a dash. */
                  <span
                    data-testid={`ibs-portfolio-underived-${r.code}`}
                    className="text-xs text-slate-500"
                  >
                    {t("screens.iBSPortfolioView.notDerived")}
                  </span>
                )}
              </td>
              {/* D2 -- "4h" alone reads as a governed figure.
                        The badge says whether anybody signed for it. */}
              <td className="px-3 py-1.5 text-xs text-slate-600 tabular-nums">
                <ToleranceBadge state={tolerance.get(r.ibs_service_id)} />
              </td>
              <td className="px-3 py-1.5">
                {r.live_verdict ? (
                  <span title={r.live_via ?? undefined}>
                    <Pill
                      value={r.live_verdict}
                      tone={VERDICT_TONE}
                      testid={`ibs-portfolio-verdict-${r.code}`}
                    />
                  </span>
                ) : (
                  /* Round 10 F3 -- a dash in a column carrying
                           "at risk" and "within" on its neighbours
                           reads as "nothing to report", and a reader
                           watching a live incident on this IBS read it
                           that way. The monitor only assesses an IBS
                           with a DECLARED tolerance: there is nothing
                           for a projection to breach, which is a fact
                           about the record rather than about the
                           service. */
                  <span
                    data-testid={`ibs-portfolio-unjudged-${r.code}`}
                    className="text-xs text-slate-500"
                  >
                    {/* Round 11 F1 -- and the two cells have to
                              agree. The round-10 sentence was written
                              for an IBS with no tolerance at all, and
                              printed unchanged beside a tolerance cell
                              reading "2h declared, awaiting the CRO":
                              one row saying both that a tolerance was
                              declared and that none was. Both facts
                              are real -- a declaration in flight puts
                              no number in force for a projection to
                              breach -- so the cell says which of the
                              two it means. */}
                    {toleranceInFlight(tolerance.get(r.ibs_service_id))
                      ? t("screens.iBSPortfolioView.notInForce")
                      : t("screens.iBSPortfolioView.notJudged")}
                  </span>
                )}
              </td>
              <td className="px-3 py-1.5 text-center tabular-nums">
                {r.open_findings}
              </td>
              <td className="px-3 py-1.5 text-xs text-slate-600">
                {r.last_drill_at ?? t("screens.iBSPortfolioView.never")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <article data-testid="ibs-portfolio-view" className="space-y-5">
      <header className="space-y-1">
        <h1 className="font-display text-2xl text-slate-900">
          <MarsadIcon name="ibs" className="me-2 inline-block h-6 w-6 align-[-4px]" />
          {t("screens.iBSPortfolioView.ibsPortfolio")}
        </h1>
        <p className="max-w-3xl text-sm text-slate-500">
          {t("screens.iBSPortfolioView.everyImportantBusinessServiceOnOneScreen")}{" "}
          <span className="font-medium">{t("screens.iBSPortfolioView.standing")}</span>{" "}
          {t("screens.iBSPortfolioView.liveToleranceVerdictFromContinuousMonitoring")}
        </p>
      </header>

      <Explainer termKey="ibs" className="max-w-3xl" />

      <MvbFindingsPanel />

      {isLoading ? (
        <div data-testid="ibs-portfolio-loading" className="text-sm text-slate-500">
          {t("screens.iBSPortfolioView.loading")}
        </div>
      ) : isError || !data ? (
        <div data-testid="ibs-portfolio-error" className="text-sm text-slate-500">
          {t("screens.iBSPortfolioView.portfolioUnavailable")}
        </div>
      ) : (
        <>
          <section
            data-testid="ibs-portfolio-summary"
            className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-paper-2 bg-white p-4 shadow-card"
          >
            <span
              data-testid="ibs-portfolio-in-register"
              className="font-display text-3xl text-slate-900 tabular-nums"
            >
              {data.in_register}
            </span>
            <span className="text-sm text-slate-600">
              {t("screens.iBSPortfolioView.inRegister")}
            </span>
            <span className="text-sm text-slate-600 tabular-nums">
              {data.under_assessment > 0 &&
                t("screens.iBSPortfolioView.underAssessment", {
                  count: data.under_assessment,
                })}{" "}
              {/* Round 19 P18 -- the admitted services, named: "3 MVB" sat
                  beside the strip's "6 in the declaration's scope". */}
              <span data-testid="ibs-portfolio-mvb">
                {t("screens.iBSPortfolioView.mvbAdmitted", { count: data.mvb })}
              </span>
            </span>
            {((data.functions_in_register ?? 0) > 0 ||
              (data.functions_under_assessment ?? 0) > 0) && (
              <Link
                to="/twin/cifs"
                data-testid="ibs-portfolio-functions-count"
                className="text-sm text-teal-800 tabular-nums hover:underline"
              >
                {t("screens.iBSPortfolioView.functionsCount", {
                  count: data.functions_in_register ?? 0,
                  under: data.functions_under_assessment ?? 0,
                })}
              </Link>
            )}
            {data.breached_now > 0 && (
              <span
                data-testid="ibs-portfolio-breached"
                className="rounded px-2 py-0.5 text-sm font-semibold text-rose-900 ring-1 ring-rose-300 bg-rose-100 tabular-nums"
              >
                {data.breached_now}{" "}
                {t("screens.iBSPortfolioView.inLiveToleranceBreach")}
              </span>
            )}
            {data.red > 0 && (
              <span className="text-sm text-rose-700 tabular-nums">
                {data.red} {t("screens.iBSPortfolioView.resilienceRed")}
              </span>
            )}
            <Link
              to="/twin/war-room"
              className="ms-auto text-sm text-brand-brassDark hover:underline"
            >
              {t("screens.iBSPortfolioView.warRoom")}
            </Link>
          </section>

          {renderTable(
            ibsRows,
            t("screens.iBSPortfolioView.ibs"),
            "ibs-portfolio-table",
          )}
          {/* Round 16 L1 -- the functions, under their own heading and
              counted apart; a removed function is on neither list. */}
          {fnRows.length > 0 && (
            <section data-testid="ibs-portfolio-functions" className="space-y-2">
              <h2 className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-slate-400">
                {t("screens.iBSPortfolioView.functionsHeading")}
              </h2>
              {renderTable(
                fnRows,
                t("screens.iBSPortfolioView.function"),
                "ibs-portfolio-functions-table",
              )}
            </section>
          )}
        </>
      )}
    </article>
  );
}
